import { TreeView, TraitLabel } from '@fluid-experimental/tree';
import { assertPrecondition, mfsError } from '../../../errors';
import { toMfsRelationship, EdgeLabelType } from '../../../store/relations';
import { getNodes, hasNone } from './deltaUtility';
import { NodeKind, MfsEdgeNode, MfsItemNode, MfsEdgeSetNode } from '..';
import { isEdgeNode, isEdgeSetNode, isItemNode } from '../typeGuards';
import { DeltaNodeGroup, Match, MfsChange } from './types';
import { MfsRelationshipChange } from '../../../store/mfsEventTypes';
import { logVerbose } from '../../../log';

/** Check whether the delta matches a relationshipChanged event. */
export function matchRelationshipChanged(
  view: TreeView,
  previousView: TreeView,
  delta: DeltaNodeGroup
): Match<MfsChange[]> {
  const hasAdd = !hasNone(delta, 'added');
  const hasRemove = !hasNone(delta, 'removed');
  const hasChanged = !hasNone(delta, 'changed');

  if (hasAdd && !hasRemove) {
    logVerbose('matchRelationshipChanged', 'add relationship match');
    return hasRelationshipChange(view, delta, 'add');
  }

  if (hasRemove && !hasAdd) {
    logVerbose('matchRelationshipChanged', 'remove relationship match');
    return hasRelationshipChange(previousView, delta, 'remove');
  }

  if (hasChanged && hasAdd && hasRemove) {
    logVerbose('matchRelationshipChanged', 'update relationship match');
    return hasRelationshipChange(view, delta, 'add', 'update');
  }

  logVerbose('matchCollectionChanged', 'match collection failed');
  return Match.fail();
}

function hasRelationshipChange(
  view: TreeView,
  delta: DeltaNodeGroup,
  nodesChangeKind: MfsRelationshipChange['changeKind'],
  changeKind?: MfsRelationshipChange['changeKind']
): Match<MfsChange[]> {
  const changes: MfsChange[] = [];

  const edgeNodes = getNodes(delta, nodesChangeKind, NodeKind.Edge);

  for (const edge of edgeNodes) {
    assertPrecondition(isEdgeNode(edge));

    const { itemNode, edgeLabel } = getParentItemNode(view, edge);

    changes.push({
      changeKind: changeKind ?? nodesChangeKind,
      kind: 'relationshipChanged',
      relatedItemId: edge.payload.neighbor,
      relationship: toMfsRelationship(edgeLabel as EdgeLabelType, edge.payload.attributes),
      itemId: itemNode.payload.id,
      label: itemNode.payload.label,
    });
  }
  logVerbose('hasRelationshipChange', 'changes length', { changesLength: changes.length });
  return changes.length > 0 ? Match.ok(changes) : Match.fail();
}

function getParentItemNode(view: TreeView, edge: MfsEdgeNode): { itemNode: MfsItemNode; edgeLabel: TraitLabel } {
  const edgeSet = view.tryGetParentViewNode(edge.identifier);
  assertPrecondition(!!edgeSet && isEdgeSetNode(edgeSet), () => mfsError.nodeNotFound(NodeKind.EdgeSet, edgeSet));
  return getParentOfEdgeSet(view, edgeSet);
}

function getParentOfEdgeSet(view: TreeView, edgeSet: MfsEdgeSetNode): { itemNode: MfsItemNode; edgeLabel: TraitLabel } {
  const { parent, label: edgeLabel } = view.getTraitLocation(edgeSet.identifier);
  const itemNode = view.tryGetViewNode(parent);
  assertPrecondition(!!itemNode && isItemNode(itemNode), () => mfsError.nodeNotFound(NodeKind.Item, itemNode));
  return { itemNode, edgeLabel };
}
